import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SparklesIcon, CheckIcon, FileSpreadsheetIcon, PlugIcon, LinkIcon, CopyIcon, ArrowRightIcon, UsersIcon } from 'lucide-react';
import { SlideFrame } from '../SlideFrame';
import { Logo } from '../../Logo';
import { TalabWindow, TalabAskBar, TalabAgentAvatar } from '../TalabWindow';
import { useSequence } from '../../../hooks/useSequence';
const EASE = [0.23, 1, 0.32, 1] as const;

/* ------------------------------------------------------------------ */
/* Simplified restagings of the real Talab onboarding surfaces.        */
/* Copy is taken verbatim from the product so the deck stays honest.   */
/* ------------------------------------------------------------------ */

function ScreenAskTalab() {
  return <div className="flex flex-col items-center justify-center h-full px-10" data-id="element-2068" data-mp="1nuz1h4poswo5">
      <div className="w-12 h-12 rounded-full bg-white shadow-soft border border-slate-100 flex items-center justify-center" data-id="element-2069" data-mp="1sm8n7u847fur">
        <SparklesIcon className="w-5 h-5 text-primary-indigo" data-id="element-2070" data-mp="1riczqo7p71km" />
      </div>
      <p className="mt-4 font-display font-bold text-[22px] text-dark-navy tracking-tight" data-id="element-2071" data-mp="1nh9pqicbzzrb">
        Ask Talab{' '}
        <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-indigo to-ai-purple" data-id="element-2072" data-mp="2197r26zswrcx">
          Anything
        </span>
      </p>
      <p className="mt-1.5 text-[11px] text-slate-500" data-id="element-2073" data-mp="1wgmy17opyiag">
        Cars, properties, jobs, services, products and more.
      </p>
      <div className="w-[420px] mt-5" data-id="element-2074" data-mp="267re6aq4s96v">
        <TalabAskBar compact focused data-id="element-2075" data-mp="266pq95o0ns7q" />
      </div>
    </div>;
}
function ScreenActivateHub() {
  return <div className="flex flex-col items-center justify-center h-full px-10 text-center" data-id="element-2076" data-mp="2k68xlw906ziz">
      <span className="px-2.5 py-1 rounded-full bg-primary-indigo/[0.07] text-[9px] font-semibold uppercase tracking-[0.16em] text-primary-indigo" data-id="element-2077" data-mp="2f9ihj8x783xk">
        Business Hub
      </span>
      <p className="mt-3 font-display font-bold text-[20px] text-dark-navy tracking-tight" data-id="element-2078" data-mp="kuy0d6836qix">
        Turn Talab into your selling workspace
      </p>
      <p className="mt-2 max-w-[440px] text-[11px] leading-relaxed text-slate-500" data-id="element-2079" data-mp="2kyidqskk1ysg">
        Activate your Business Hub to publish listings, capture leads, and get your own shareable
        shop link to send to customers — all in one place.
      </p>
      <motion.button type="button" className="mt-4 px-5 py-2.5 rounded-xl bg-gradient-to-r from-primary-indigo to-ai-purple text-white text-[12px] font-semibold shadow-lg shadow-primary-indigo/25" animate={{
      scale: [1, 1.03, 1]
    }} transition={{
      duration: 1.6,
      repeat: Infinity,
      ease: 'easeInOut',
      repeatDelay: 0.4
    }} data-id="element-2080" data-mp="2ywyn6r9sy3ea">
        Activate Business Hub
      </motion.button>
      <p className="mt-2 text-[9.5px] text-slate-400" data-id="element-2081" data-mp="3ikpko94c037z">Free to start · No credit card required</p>
      <div className="grid grid-cols-3 gap-2.5 w-full mt-5" data-id="element-2082" data-mp="3mmsy33ak9hvw">
        {[{
        title: 'Unified catalogue',
        body: 'One place for every listing.'
      }, {
        title: 'Your own shop link',
        body: 'Share it with customers.'
      }, {
        title: 'Leads & team',
        body: 'Capture and assign enquiries.'
      }].map(perk => <div key={perk.title} className="rounded-xl border border-slate-100 bg-slate-50/60 p-2.5 text-left" data-id="element-2083" data-mp="1axcz1irzokt5">
            <p className="text-[10.5px] font-semibold text-dark-navy" data-id="element-2084" data-mp="16z6w6lu9updi">{perk.title}</p>
            <p className="mt-0.5 text-[9px] text-slate-400" data-id="element-2085" data-mp="11zua43b85mbz">{perk.body}</p>
          </div>)}
      </div>
    </div>;
}
function ScreenAIAssist() {
  return <div className="flex flex-col justify-center h-full px-8" data-id="element-2086" data-mp="1umf132nsfwf0">
      <div className="flex gap-3" data-id="element-2087" data-mp="1pvyofkrsketp">
        <TalabAgentAvatar data-id="element-2088" data-mp="1pv5gigh3jj9z" />
        <div className="bg-white rounded-2xl rounded-tl-none px-4 py-3 shadow-soft border border-slate-100" data-id="element-2089" data-mp="1pudpliyg29q1">
          <p className="text-[12.5px] leading-relaxed text-slate-700" data-id="element-2090" data-mp="1kwls89f15veg">
            Hi 👋 I&apos;m Talab AI. Let&apos;s build your Catalog. How would you like to start?
          </p>
        </div>
      </div>
      <div className="flex gap-2.5 mt-4 pl-[40px]" data-id="element-2091" data-mp="2djg8eu4te8m4">
        {['Upload an Excel sheet', 'Connect website / API', 'Add my first item'].map((chip, index) => <motion.span key={chip} className="px-3.5 py-2 bg-white border border-slate-200 rounded-xl text-[11px] font-medium text-slate-600" initial={{
        opacity: 0,
        y: 8
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6,
        ease: EASE,
        delay: 0.7 + index * 0.4
      }} data-id="element-2092" data-mp="225whz0cxos1j" data-mpi={index}>
              {chip}
            </motion.span>)}
      </div>
      <p className="mt-5 pl-[40px] text-[10px] text-slate-400" data-id="element-2093" data-mp="2kxffiro6vdw6">
        Composer · “Ask Talab a question or describe what you want to add…”
      </p>
    </div>;
}
function ScreenImport() {
  return <div className="flex flex-col justify-center h-full px-8" data-id="element-2094" data-mp="2zsvf1c7jg1zy">
      <div className="grid grid-cols-2 gap-4" data-id="element-2095" data-mp="2zram1r0vgr47">
        <div className="rounded-2xl border-2 border-dashed border-primary-indigo/30 bg-primary-indigo/[0.03] p-4" data-id="element-2096" data-mp="34jbtmzn3htnw">
          <div className="flex items-center gap-2" data-id="element-2097" data-mp="3dxlyxcp91zfz">
            <FileSpreadsheetIcon className="w-4 h-4 text-primary-indigo" data-id="element-2098" data-mp="3dwat77c8uhm8" />
            <p className="text-[11.5px] font-semibold text-dark-navy" data-id="element-2099" data-mp="39wai9wocsojx">Upload an Excel sheet</p>
          </div>
          <p className="mt-2 text-[10px] leading-snug text-slate-500" data-id="element-2100" data-mp="3nnf8y99qu6i4">
            Select your Excel (.xlsx) or CSV file. I will automatically map your columns to the
            catalog format.
          </p>
          <span className="inline-flex mt-3 px-3 py-1.5 rounded-lg bg-primary-indigo text-white text-[10.5px] font-semibold" data-id="element-2101" data-mp="2v1m47u4w0hsp">
            Select File
          </span>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-4" data-id="element-2102" data-mp="34za9vf4179pn">
          <div className="flex items-center gap-2" data-id="element-2103" data-mp="2zxkyfycwcu11">
            <PlugIcon className="w-4 h-4 text-ai-purple" data-id="element-2104" data-mp="300yzn39tqx6w" />
            <p className="text-[11.5px] font-semibold text-dark-navy" data-id="element-2105" data-mp="k66t1uyktgq1">Connect website / API</p>
          </div>
          <p className="mt-2 text-[10px] leading-snug text-slate-500" data-id="element-2106" data-mp="f7vi1gwjp068">
            Enter your website URL or API endpoint, and I will scan and import your inventory
            automatically.
          </p>
          <div className="flex items-center gap-2 mt-3" data-id="element-2107" data-mp="r8v8z5chcmi1">
            <span className="flex-1 h-7 px-2.5 rounded-lg border border-slate-200 flex items-center text-[10px] text-slate-300" data-id="element-2108" data-mp="w9huohi135sb">
              https://yourwebsite.com
            </span>
            <span className="px-3 py-1.5 rounded-lg bg-dark-navy text-white text-[10.5px] font-semibold" data-id="element-2109" data-mp="3ldkbioii4ja">
              Connect
            </span>
          </div>
        </div>
      </div>

      <div className="mt-5 px-4 py-3 rounded-xl bg-white border border-slate-100 shadow-soft" data-id="element-2110" data-mp="dj9y6i8u3dl3">
        <p className="text-[10.5px] text-slate-500" data-id="element-2111" data-mp="ia035udclef9">Analyzing your file and mapping the columns…</p>
        <div className="relative h-1 mt-2 rounded-full bg-slate-100 overflow-hidden" data-id="element-2112" data-mp="3h00652nz80pf">
          <motion.div className="absolute inset-y-0 left-0 rounded-full bg-gradient-to-r from-primary-indigo to-ai-purple" initial={{
          width: '8%'
        }} animate={{
          width: '92%'
        }} transition={{
          duration: 3.2,
          ease: EASE
        }} data-id="element-2113" data-mp="3gy5w4je8tt04" />
        </div>
      </div>
    </div>;
}
function ScreenPublished() {
  return <div className="flex flex-col justify-center h-full px-8" data-id="element-2114" data-mp="34g382ueprx9b">
      <div className="rounded-2xl bg-white border border-slate-100 shadow-soft p-4" data-id="element-2115" data-mp="2zijs9q2dbcf9">
        <p className="text-[11.5px] text-slate-600" data-id="element-2116" data-mp="2vgpy80moh6o2">
          Done — I&apos;ve applied those fees and rules. Here&apos;s a summary of your import:
        </p>
        <div className="grid grid-cols-3 gap-2.5 mt-3" data-id="element-2117" data-mp="2qoezv27l5sxm">
          {[{
          label: 'Cars added',
          value: '24'
        }, {
          label: 'With photos',
          value: '18'
        }, {
          label: 'No photos',
          value: '6'
        }].map(stat => <div key={stat.label} className="rounded-xl border border-slate-100 bg-slate-50/60 px-3 py-2" data-id="element-2118" data-mp="2bsh59odb8wni">
              <p className="font-display font-bold text-[16px] text-dark-navy leading-none" data-id="element-2119" data-mp="27qlvrwl87cyn">
                {stat.value}
              </p>
              <p className="mt-1 text-[9px] uppercase tracking-[0.14em] text-slate-400" data-id="element-2120" data-mp="3vocvk8vuh54m">
                {stat.label}
              </p>
            </div>)}
        </div>
        <div className="flex items-center gap-2 mt-3" data-id="element-2121" data-mp="3lom2mbmxmy6n">
          <span className="px-3 py-1.5 rounded-lg bg-gradient-to-r from-primary-indigo to-ai-purple text-white text-[10.5px] font-semibold" data-id="element-2122" data-mp="3qoqjw3vnzwxx">
            Publish all 24 (with &amp; without photos)
          </span>
          <span className="px-3 py-1.5 rounded-lg border border-slate-200 text-[10.5px] font-medium text-slate-500" data-id="element-2123" data-mp="3hnsf3jdejlea">
            Publish only the 18 with photos
          </span>
        </div>
      </div>

      <div className="mt-3 space-y-1.5" data-id="element-2124" data-mp="37xpmhka709gh">
        {['Toyota Prado GXR 2018', 'Nissan Patrol 2020', 'Toyota Yaris 2022'].map((item, index) => <motion.div key={item} className="flex items-center gap-3 px-3 py-2 rounded-xl bg-white border border-slate-100" initial={{
        opacity: 0,
        x: -8
      }} animate={{
        opacity: 1,
        x: 0
      }} transition={{
        duration: 0.6,
        ease: EASE,
        delay: 0.75 + index * 0.42
      }} data-id="element-2125" data-mp="2tyzom2fju5r2" data-mpi={index}>
            <span className="w-6 h-6 rounded-md bg-slate-100" data-id="element-2126" data-mp="2af0u85f52opg" />
            <p className="flex-1 text-[10.5px] font-medium text-dark-navy" data-id="element-2127" data-mp="2oe0j9e6kd5m5">{item}</p>
            <span className="px-2 py-0.5 rounded-md bg-green-600 text-white text-[8.5px] font-bold uppercase tracking-wide" data-id="element-2128" data-mp="2jcb87yjccry2">
              On Talab
            </span>
          </motion.div>)}
      </div>
    </div>;
}
function ScreenStorefront() {
  return <div className="flex flex-col justify-center h-full px-8" data-id="element-2129" data-mp="372oz3lq6wkr2">
      <div className="flex items-center gap-3 px-4 py-3 rounded-2xl bg-white border border-slate-100 shadow-soft" data-id="element-2130" data-mp="3751eps8h6j0y">
        <LinkIcon className="w-4 h-4 text-primary-indigo" data-id="element-2131" data-mp="333pp3mplpxsr" />
        <div className="flex-1 min-w-0" data-id="element-2132" data-mp="3h1w9q15ji1ah">
          <p className="text-[9px] uppercase tracking-[0.16em] text-slate-400" data-id="element-2133" data-mp="3c21mc7kz3tu6">Your shop link</p>
          <p className="text-[12px] font-semibold text-dark-navy" data-id="element-2134" data-mp="3e58wi7drzy1l">talab.app/shop/easylease</p>
        </div>
        <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-dark-navy text-white text-[10.5px] font-semibold" data-id="element-2135" data-mp="39ek2olemioza">
          <CopyIcon className="w-3 h-3" data-id="element-2136" data-mp="35c5ju1ayfp80" /> Copy link
        </span>
      </div>

      <div className="mt-4 rounded-2xl bg-white border border-slate-100 shadow-soft p-4" data-id="element-2137" data-mp="30e451skkac2c">
        <div className="flex items-center gap-2" data-id="element-2138" data-mp="yqrmm32a5pm">
          <Logo size="sm" data-id="element-2139" data-mp="y75et6dov5s" />
          <p className="text-[11px] font-semibold text-dark-navy" data-id="element-2140" data-mp="we9o8xazvck">EasyLease Rentals</p>
          <span className="px-2 py-0.5 rounded-full bg-success/10 text-[8.5px] font-semibold text-success" data-id="element-2141" data-mp="3t3b54vznz9pp">
            Live
          </span>
        </div>
        <div className="flex gap-3 mt-3" data-id="element-2142" data-mp="3j54zq4enflg3">
          <TalabAgentAvatar size={24} data-id="element-2143" data-mp="3j43mbta5umv7" />
          <div className="bg-slate-50 rounded-2xl rounded-tl-none px-3 py-2" data-id="element-2144" data-mp="2cnzypdfzny7n">
            <p className="text-[11px] text-slate-600" data-id="element-2145" data-mp="2clx4sibptkxn">
              Welcome to EasyLease. What kind of car do you need, and for how long?
            </p>
          </div>
        </div>
        <div className="mt-3" data-id="element-2146" data-mp="1xj5dxaw7fna1">
          <TalabAskBar compact placeholder="Ask EasyLease anything…" data-id="element-2147" data-mp="22k33i8jjnjlk" />
        </div>
      </div>
    </div>;
}
function ScreenLeads() {
  return <div className="flex flex-col justify-center h-full px-8" data-id="element-2148" data-mp="27huy5abkpjtg">
      <p className="font-display font-bold text-[16px] text-dark-navy" data-id="element-2149" data-mp="2canxtjs6q1yo">Leads</p>
      <p className="text-[10.5px] text-slate-500" data-id="element-2150" data-mp="2gbzug99h7ngd">
        Qualified customer opportunities identified by Talab AI
      </p>
      <div className="flex items-center gap-2 mt-3" data-id="element-2151" data-mp="2la1bypr97epq">
        {[{
        label: 'New',
        value: '12'
      }, {
        label: 'Qualified',
        value: '9'
      }, {
        label: 'Hot',
        value: '3'
      }, {
        label: 'Follow-up',
        value: '5'
      }, {
        label: 'Won',
        value: '6'
      }].map(metric => <div key={metric.label} className="flex items-baseline gap-1.5 px-2.5 py-1.5 rounded-lg bg-white border border-slate-100" data-id="element-2152" data-mp="vwxuif8w3hq3">
            <span className="font-display font-bold text-[12px] text-dark-navy" data-id="element-2153" data-mp="su1giypvjjuh">{metric.value}</span>
            <span className="text-[9px] text-slate-400" data-id="element-2154" data-mp="nvggp0ejb26k">{metric.label}</span>
          </div>)}
      </div>
      <div className="mt-3 space-y-2" data-id="element-2155" data-mp="12sr1x1gzbmfh">
        {[{
        name: 'Omar Haddad',
        note: 'Prado · AED 100/day · 7 days',
        pill: 'Ready To Buy',
        tone: 'bg-green-100 text-green-700'
      }, {
        name: 'Sara Nasser',
        note: 'Yaris · weekly rate enquiry',
        pill: 'High Intent',
        tone: 'bg-primary-indigo/10 text-primary-indigo'
      }].map((lead, index) => <motion.div key={lead.name} className="flex items-center gap-3 px-3.5 py-2.5 rounded-xl bg-white border border-slate-200/60" initial={{
        opacity: 0,
        y: 8
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6,
        ease: EASE,
        delay: 0.75 + index * 0.42
      }} data-id="element-2156" data-mp="16s86ztshuhk4" data-mpi={index}>
            <span className="w-7 h-7 rounded-full bg-gradient-to-br from-primary-indigo to-ai-purple" data-id="element-2157" data-mp="1qiutgagw7vq4" />
            <div className="flex-1 min-w-0" data-id="element-2158" data-mp="297f222zbhqku">
              <p className="text-[11px] font-semibold text-dark-navy" data-id="element-2159" data-mp="2949a8fzb0alf">{lead.name}</p>
              <p className="text-[9.5px] text-slate-400" data-id="element-2160" data-mp="2e4nls80462o7">{lead.note}</p>
            </div>
            <span className={`px-1.5 py-0.5 rounded text-[8.5px] font-bold ${lead.tone}`} data-id="element-2161" data-mp="2055rxe7ynz7z">
              {lead.pill}
            </span>
          </motion.div>)}
      </div>
    </div>;
}
function ScreenTeam() {
  return <div className="flex flex-col justify-center h-full px-8" data-id="element-2162" data-mp="1qe1c6cagoac3">
      <div className="rounded-2xl bg-white border border-slate-100 shadow-soft p-4" data-id="element-2163" data-mp="1v7ckyiyu316n">
        <div className="flex items-center gap-2" data-id="element-2164" data-mp="1hg7il942zusm">
          <UsersIcon className="w-4 h-4 text-primary-indigo" data-id="element-2165" data-mp="1hdd21so6uu4h" />
          <p className="font-display font-bold text-[13px] text-dark-navy" data-id="element-2166" data-mp="1lhay1ui7votb">Invite team member</p>
        </div>
        <p className="mt-1 text-[10px] text-slate-500" data-id="element-2167" data-mp="17hs2e16le5xw">
          Choose a role, then fine-tune what they can do in each module.
        </p>

        <div className="grid grid-cols-2 gap-2.5 mt-3" data-id="element-2168" data-mp="2et9bl64szfgu">
          {[{
          label: 'Full name',
          value: 'e.g. Layla Ahmed'
        }, {
          label: 'Email',
          value: 'name@email.com'
        }].map(field => <div key={field.label} data-id="element-2169" data-mp="260e74en5tns4">
              <p className="text-[9px] uppercase tracking-[0.14em] text-slate-400" data-id="element-2170" data-mp="25zcui5qhwt8a">{field.label}</p>
              <div className="h-7 mt-1 px-2.5 rounded-lg border border-slate-200 flex items-center text-[10px] text-slate-300" data-id="element-2171" data-mp="211uig6h6lxw2">
                {field.value}
              </div>
            </div>)}
        </div>

        <div className="grid grid-cols-4 gap-2 mt-3" data-id="element-2172" data-mp="1r69hmbnun3nw">
          {[{
          role: 'Admin',
          note: 'Full access'
        }, {
          role: 'Manager',
          note: 'No billing'
        }, {
          role: 'Sales',
          note: 'Works leads'
        }, {
          role: 'Viewer',
          note: 'Read-only'
        }].map((option, index) => <div key={option.role} className={`px-2.5 py-2 rounded-xl border ${index === 1 ? 'border-primary-indigo bg-indigo-50' : 'border-slate-200'}`} data-id="element-2173" data-mp="3chj50wqiwuwc" data-mpi={index}>
              <p className={`text-[10.5px] font-semibold ${index === 1 ? 'text-primary-indigo' : 'text-dark-navy'}`} data-id="element-2174" data-mp="3oytx39sjmqwc">
                {option.role}
              </p>
              <p className="text-[8.5px] text-slate-400" data-id="element-2175" data-mp="2jhn98a288n1">{option.note}</p>
            </div>)}
        </div>

        <div className="flex items-center justify-end gap-2 mt-3.5" data-id="element-2176" data-mp="3149cakmn85yc">
          <span className="px-3 py-1.5 rounded-lg border border-slate-200 text-[10.5px] font-medium text-slate-500" data-id="element-2177" data-mp="361j6seow37t1">
            Cancel
          </span>
          <span className="px-3 py-1.5 rounded-lg bg-primary-indigo text-white text-[10.5px] font-semibold" data-id="element-2178" data-mp="3f27d0n81k3vd">
            Send Invite
          </span>
        </div>
      </div>
    </div>;
}
function ScreenWidget() {
  return <div className="flex flex-col justify-center h-full px-8" data-id="element-2179" data-mp="ua5jc5wvmntu">
      <div className="flex items-center justify-between" data-id="element-2180" data-mp="2rbun79q9208l">
        <div data-id="element-2181" data-mp="2ra9s9j00wfa7">
          <p className="text-[9px] uppercase tracking-[0.16em] text-slate-400" data-id="element-2182" data-mp="2rdxlsi5gx1ox">Step 8 of 8</p>
          <p className="font-display font-bold text-[13px] text-dark-navy" data-id="element-2183" data-mp="2mf45khysqwl2">Deploy · Go live</p>
        </div>
        <span className="px-3 py-1.5 rounded-lg bg-gradient-to-r from-primary-indigo to-ai-purple text-white text-[10.5px] font-semibold" data-id="element-2184" data-mp="30aexbmhqv1l7">
          Deploy Agent
        </span>
      </div>

      <div className="relative mt-3 rounded-2xl bg-slate-900 p-3.5" data-id="element-2185" data-mp="27m10ha2e6bb8">
        <pre className="text-green-300 font-mono text-[9.5px] leading-relaxed" data-id="element-2186" data-mp="23ntfu44t4pur">{`<script src="https://cdn.talab.ai/widget.js"
  data-agent="easylease-rentals"
  data-key="pk_live_8f2a..."
  async>
</script>`}</pre>
        <motion.span className="absolute top-2.5 right-2.5 flex items-center gap-1 px-2 py-1 rounded-md bg-white/10 text-white text-[9px] font-medium" animate={{
        opacity: [1, 1, 0.4, 1]
      }} transition={{
        duration: 2.4,
        repeat: Infinity,
        ease: 'linear'
      }} data-id="element-2187" data-mp="2clvpt96iqsu6">
          <CopyIcon className="w-2.5 h-2.5" data-id="element-2188" data-mp="3vkbkuiqrem59" /> Copy
        </motion.span>
      </div>

      <p className="mt-3 text-[9px] uppercase tracking-[0.16em] text-slate-400" data-id="element-2189" data-mp="480e5qx89iu2">
        One-click installs
      </p>
      <div className="flex items-center gap-2 mt-2" data-id="element-2190" data-mp="3mn16goinagw4">
        {['WordPress', 'Shopify', 'Wix', 'Webflow', 'Custom Website'].map(platform => <span key={platform} className="px-3 py-1.5 rounded-xl bg-white border border-slate-200 text-[10px] font-medium text-slate-600" data-id="element-2191" data-mp="j4r65f4kns9n">
            {platform}
          </span>)}
      </div>
    </div>;
}

/* ------------------------------------------------------------------ */

interface OnboardingStep {
  id: string;
  label: string;
  url: string;
  badge: string;
  render: () => React.ReactNode;
}
const STEPS: OnboardingStep[] = [{
  id: 'visit',
  label: 'Go to Talab.app',
  url: 'talab.app',
  badge: 'Discover',
  render: () => <ScreenAskTalab data-id="element-2192" data-mp="2ytj2tfk9mk0u" />
}, {
  id: 'activate',
  label: 'Activate Business Hub',
  url: 'talab.app/business',
  badge: 'Business Hub',
  render: () => <ScreenActivateHub data-id="element-2193" data-mp="3lj2c3uj48vb7" />
}, {
  id: 'assist',
  label: 'Talab AI assists onboarding',
  url: 'talab.app/business/catalog',
  badge: 'Talab AI',
  render: () => <ScreenAIAssist data-id="element-2194" data-mp="3bm65jucs2j9a" />
}, {
  id: 'import',
  label: 'Upload by Excel or connect API',
  url: 'talab.app/business/catalog',
  badge: 'Import',
  render: () => <ScreenImport data-id="element-2195" data-mp="d6dbvbfiy616" />
}, {
  id: 'published',
  label: 'Inventory prepared and published',
  url: 'talab.app/business/catalog',
  badge: 'Catalog',
  render: () => <ScreenPublished data-id="element-2196" data-mp="3cwe5wc9o6s8" />
}, {
  id: 'storefront',
  label: 'Storefront becomes ready',
  url: 'talab.app/shop/easylease',
  badge: 'AI Storefront',
  render: () => <ScreenStorefront data-id="element-2197" data-mp="vv1ola3ut51v" />
}, {
  id: 'leads',
  label: 'Lead generation module',
  url: 'talab.app/business/leads',
  badge: 'Leads',
  render: () => <ScreenLeads data-id="element-2198" data-mp="r28kmog7m0qu" />
}, {
  id: 'team',
  label: 'Invite your team',
  url: 'talab.app/business/settings',
  badge: 'Team access',
  render: () => <ScreenTeam data-id="element-2199" data-mp="13f2m5t3mnz76" />
}, {
  id: 'widget',
  label: 'Generate the website widget',
  url: 'talab.app/business/agents',
  badge: 'Widget',
  render: () => <ScreenWidget data-id="element-2200" data-mp="1dc7bu2womcsq" />
}];
export function SlideOnboarding({
  active
}: {
  active: boolean;
}) {
  // Each screen holds long enough to be read before the next one focuses.
  const step = useSequence(STEPS.slice(1).map((_, index) => index === 0 ? 4200 : 4000), active);
  const index = Math.min(step, STEPS.length - 1);
  const current = STEPS[index];
  return <SlideFrame eyebrow="Onboarding" title="Even onboarding is powered by AI" lead="From setup to go-live, Talab helps businesses launch fast." footnote={<div className="flex items-end justify-between gap-10" data-id="element-2202" data-mp="213w63ot2dbcb">
          <p className="text-[12px] leading-relaxed text-slate-500 max-w-[560px]" data-id="element-2203" data-mp="2627hi3z0btwn">
            Activate your Business Hub, let Talab AI help you upload or connect your inventory, and
            go live with your storefront, leads, and widget in a streamlined onboarding flow.
          </p>
          <p className="shrink-0 font-display font-bold text-[15px] text-dark-navy" data-id="element-2204" data-mp="3ehok99vq1sfr">
            With Talab, even onboarding is{' '}
            <span className="text-primary-indigo" data-id="element-2205" data-mp="3jim0hgx68cem">conversational.</span>
          </p>
        </div>} data-id="element-2201" data-mp="1r67za0i6odds">
      <div className="h-full flex gap-8" data-id="element-2206" data-mp="3nicqhievioi3">
        <ol className="w-[268px] shrink-0" data-id="element-2207" data-mp="3sh7fj23fp9an">
          {STEPS.map((item, itemIndex) => {
          const isActive = itemIndex === index;
          const isDone = itemIndex < index;
          return <li key={item.id} data-id="element-2208" data-mp="62eb5htezmyh" data-mpi={itemIndex}>
                <motion.div className="flex items-center gap-2.5 px-3 py-[7px] rounded-xl border" animate={{
              backgroundColor: isActive ? 'rgba(67,56,202,0.05)' : 'rgba(255,255,255,0)',
              borderColor: isActive ? 'rgba(67,56,202,0.25)' : 'rgba(255,255,255,0)'
            }} transition={{
              duration: 0.5,
              ease: EASE
            }} data-id="element-2209" data-mp="61lexas30ful">
                  <motion.span className="w-4 h-4 rounded-full flex items-center justify-center shrink-0" animate={{
                backgroundColor: isDone ? '#10B981' : isActive ? '#4338CA' : '#E2E8F0'
              }} transition={{
                duration: 0.45,
                ease: EASE
              }} data-id="element-2210" data-mp="1qk5m4lf2a2zc">
                    {isDone ? <CheckIcon className="w-2.5 h-2.5 text-white" strokeWidth={3} data-id="element-2211" data-mp="29goq6wb0d0p1" /> : <span className="text-[8px] font-bold text-white" data-id="element-2212" data-mp="31zc7s1qspxld">{itemIndex + 1}</span>}
                  </motion.span>
                  <motion.span className="text-[12px] leading-snug" animate={{
                color: isActive ? '#0B1220' : isDone ? '#475569' : '#94A3B8'
              }} transition={{
                duration: 0.45,
                ease: EASE
              }} data-id="element-2213" data-mp="2x7u4h71avkx8">
                    {item.label}
                  </motion.span>
                </motion.div>
              </li>;
        })}
        </ol>

        <div className="relative flex-1 min-w-0" data-id="element-2214" data-mp="bncf4ncpvfqb">
          <TalabWindow url={current.url} storeBadge={current.badge} className="absolute inset-0" data-id="element-2215" data-mp="bljafdlnz5ky">
            <AnimatePresence mode="wait" data-id="element-2216" data-mp="2mo0prjevdcw">
              <motion.div key={current.id} className="absolute inset-0" initial={{
              opacity: 0,
              scale: 0.975
            }} animate={{
              opacity: 1,
              scale: 1
            }} exit={{
              opacity: 0,
              scale: 1.012
            }} transition={{
              duration: 0.7,
              ease: EASE
            }} data-id="element-2217" data-mp="2npngt9shoag">
                {current.render()}
              </motion.div>
            </AnimatePresence>
          </TalabWindow>

          <motion.span key={`badge-${current.id}`} className="absolute -top-3 right-4 px-2.5 py-1 rounded-full bg-dark-navy text-white text-[9px] font-semibold uppercase tracking-[0.16em] shadow-large" initial={{
          opacity: 0,
          y: -6
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.28,
          ease: EASE
        }} data-id="element-2218" data-mp="274m7mhwi89gh">
            Step {index + 1} of {STEPS.length}
          </motion.span>
        </div>
      </div>
    </SlideFrame>;
}